/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 15/07/2025 13:32
 *
 */

import {
  AfterViewInit,
  computed,
  Directive,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { CartographyResourceBbx } from '../../model/cartography-resource/cartography-resource-bbx';
import { CartographyResourceMat } from '../../model/cartography-resource/cartography-resource-mat';
import { CartographyResourceSite } from '../../model/cartography-resource/cartography-resource-site';
import { SharedMapSettingsService } from '../../service/shared-map-settings.service';
import { AoPath } from '../../model/path/ao-path';
import {
  Cluster,
  GoogleMap,
  MapAdvancedMarker,
  MapInfoWindow,
} from '@angular/google-maps';
import {
  GmapsAreaCircle,
  GmapsMarker,
  GmapsPoint,
} from '@infodata-group/inf-commons-tools';
import {
  applyButtonStyleIsSelected,
  BUTTON_TRAFFIC,
} from './map.abstract.constants';
import { DataModel, StandardAction } from '@infodata-group/inf-commons';
import { CartographyResource } from '../../model/cartography-resource/cartography-resource';

const INFO_WINDOW_OPTIONS: google.maps.InfoWindowOptions = {
  disableAutoPan: true,
  zIndex: 1,
  headerDisabled: true,
};

@Directive()
export class MapAbstract implements AfterViewInit {
  protected mapSettingsService = inject(SharedMapSettingsService);

  protected readonly infoWindowOptions = signal(INFO_WINDOW_OPTIONS);

  // Traffic part
  protected readonly buttonTraffic =
    signal<HTMLButtonElement>(BUTTON_TRAFFIC());
  protected trafficLayout = new google.maps.TrafficLayer();

  public height = input<string>('100%');
  public width = input<string>('100%');

  public bbx = input<CartographyResourceBbx[]>([]);
  public bbxModel = input<DataModel>(null);
  protected currentBbxModel = linkedSignal(this.bbxModel);
  protected bbxMarkers = viewChildren<MapAdvancedMarker>('bbxMarker');
  protected bbxInfoWindows = viewChildren<MapInfoWindow>('bbxInfoWindow');

  public tags = input<CartographyResourceMat[]>([]);
  public tagModel = input<DataModel>(null);
  protected currentTagModel = linkedSignal(this.tagModel);
  protected tagMarkers = viewChildren<MapAdvancedMarker>('tagMarker');
  protected tagInfoWindows = viewChildren<MapInfoWindow>('tagInfoWindow');

  public sites = input<CartographyResourceSite[]>([]);
  // public siteModel = input<DataModel>(null);

  public aoPath = input<AoPath>(null);

  public boundsOn = input<string[]>([]);

  public markers = input<GmapsMarker[]>([]);
  public geofenceCircles = input<GmapsAreaCircle[]>([]);
  public geofencePolygons = input<google.maps.Polygon[]>([]);

  public path = input<google.maps.Polyline[]>([]);

  public rightClickActions = input<StandardAction[]>([]);

  public themeId = signal<string>('');
  public options = input<google.maps.MapOptions>({});

  protected currentOptions = computed(() => {
    return {
      center: new google.maps.LatLng(49.8157635, 6.1315139999999815),
      mapId: this.themeId(),
      mapTypeId: this.mapSettingsService.mapTypeControl(),
      zoom: this.mapSettingsService.defaultZoom(),
      ...this.options(),
    };
  });

  public googleMap = viewChild.required(GoogleMap);

  public bbxChange = output<CartographyResourceBbx[]>();
  public materialsChange = output<CartographyResourceMat[]>();
  public sitesChange = output<CartographyResourceSite[]>();
  public aoPathChange = output<AoPath>();
  public markersChange = output<GmapsMarker[]>();
  public geofenceCirclesChange = output<GmapsAreaCircle[]>();
  public geofencePolygonsChange = output<google.maps.Polygon[]>();

  public bbxMarkerClick = output<{
    index: number;
    resource: CartographyResourceBbx;
  }>();
  public bbxInfoWindowClick = output<{
    index: number;
    resource: CartographyResourceBbx;
  }>();

  public tagMarkerClick = output<{
    index: number;
    resource: CartographyResourceMat;
  }>();
  public tagInfoWindowClick = output<{
    index: number;
    resource: CartographyResourceMat;
  }>();

  public geofenceClick = output<{
    index: number;
    resource: CartographyResourceSite;
    event: google.maps.MapMouseEvent;
  }>();

  public mapClick = output<google.maps.MapMouseEvent>();
  public mapDragStart = output<void>();
  public circleDragEnd = output<GmapsAreaCircle>();
  public mapInitialized = output<google.maps.Map>();
  public clusterClick = output<Cluster>();
  public mapRightClick = output<google.maps.MapMouseEvent>();

  constructor() {
    this.themeId.set(this.mapSettingsService.mapThemeGoogleId());
    if (!window['desktopMapComponent']) {
      window['desktopMapComponent'] = this;
    }
    effect(() => {
      if (this.bbx()) {
        this.bbxChange.emit(this.bbx());
        this.fitBounds();
      }
    });
    effect(() => {
      if (this.tags()) {
        this.materialsChange.emit(this.tags());
        this.fitBounds();
      }
    });
    effect(() => {
      if (this.sites()) {
        this.sitesChange.emit(this.sites());
        this.fitBounds();
      }
    });
    effect(() => {
      if (this.aoPath()) {
        this.aoPathChange.emit(this.aoPath());
        this.fitBounds();
      }
    });
    effect(() => {
      if (this.markers()) {
        this.markersChange.emit(this.markers());
        this.fitBounds();
      }
    });
    effect(() => {
      if (this.geofenceCircles()) {
        this.geofenceCirclesChange.emit(this.geofenceCircles());
        this.fitBounds();
      }
    });
    effect(() => {
      if (this.geofencePolygons()) {
        this.geofencePolygonsChange.emit(this.geofencePolygons());
        this.fitBounds();
      }
    });
  }

  ngAfterViewInit() {
    this.enableTraffic();
    this.fitBounds();
  }

  protected fitBounds(): void {
    if (!this.googleMap().googleMap) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    if (this.boundsOn().includes('bbx') && this.bbx()) {
      this.bbx().forEach((bbx) => {
        const latLng = new google.maps.LatLng(bbx.latitude, bbx.longitude);
        bounds.extend(latLng);
      });
    }
    if (this.boundsOn().includes('tags') && this.tags()) {
      this.tags().forEach((tag) => {
        if (tag.latitude && tag.longitude) {
          const latLng = new google.maps.LatLng(tag.latitude, tag.longitude);
          bounds.extend(latLng);
        }
      });
    }
    if (this.boundsOn().includes('markers') && this.markers()) {
      this.markers().forEach((marker) => {
        bounds.extend(marker.googleMapsLatLng());
      });
    }
    if (this.boundsOn().includes('aoPath') && this.aoPath()) {
      this.aoPath().pathPoint.forEach((point) =>
        bounds.extend(new google.maps.LatLng(point.latitude, point.longitude)),
      );
    }
    if (this.boundsOn().includes('path') && this.path()) {
      this.path().forEach((polyline) => {
        polyline.getPath().forEach((latLng) => {
          bounds.extend(latLng);
        });
      });
    }
    if (this.boundsOn().includes('geofenceCircles') && this.geofenceCircles()) {
      this.geofenceCircles()
        .map(
          (c) =>
            new google.maps.Circle({ ...c.options, center: c.center.latLng }),
        )
        .forEach((circle) => {
          const circleBounds = circle.getBounds();
          if (circleBounds) {
            bounds.union(circleBounds);
          }
        });
    }
    if (bounds.isEmpty()) {
      return;
    }

    const circle = new google.maps.Circle({
      radius: 1000,
      center: bounds.getCenter(),
    });
    bounds.union(circle.getBounds());
    this.googleMap().googleMap.fitBounds(bounds);
  }

  protected onCircleDrag(
    $event: google.maps.MapMouseEvent,
    circle: GmapsAreaCircle,
  ) {
    circle.points = new GmapsPoint(
      circle.points.id,
      $event.latLng.lat(),
      $event.latLng.lng(),
    );
  }

  private enableTraffic(): void {
    if (!this.googleMap()) {
      return;
    }
    if (this.mapSettingsService.isTrafficShown()) {
      applyButtonStyleIsSelected(this.buttonTraffic(), true);
      this.trafficLayout.setMap(this.googleMap().googleMap);
    }
    this.buttonTraffic().addEventListener('click', () => {
      const isDisplayed = this.mapSettingsService.isTrafficShown();
      applyButtonStyleIsSelected(this.buttonTraffic(), !isDisplayed);
      this.trafficLayout.setMap(
        isDisplayed ? null : this.googleMap().googleMap,
      );
      this.mapSettingsService.isTrafficShown.set(!isDisplayed);
    });
    this.googleMap().googleMap.controls[
      google.maps.ControlPosition.TOP_LEFT
    ].push(this.buttonTraffic());
  }

  protected onMapTypeIdChanged() {
    this.mapSettingsService.mapTypeControl.set(
      this.googleMap().googleMap.getMapTypeId(),
    );
  }

  protected onClusterClick($event: Cluster) {
    this.googleMap().fitBounds($event.bounds);
    this.clusterClick.emit($event);
  }

  protected onMapRightClick($event: google.maps.MapMouseEvent) {
    this.mapRightClick.emit($event);
  }

  protected onMapClick($event: google.maps.MapMouseEvent) {
    this.mapClick.emit($event);
  }

  protected onMapDragStart() {
    this.mapDragStart.emit();
  }

  protected onClickBbxMarker(index: number) {
    const resource = this.bbx().at(index);
    this.bbxMarkerClick.emit({
      index,
      resource: resource,
    });
    const infoWindow = this.bbxInfoWindows().at(index);
    if (!infoWindow) {
      return;
    }
    if (infoWindow.infoWindow.isOpen && resource.isInfoWindowLocked) {
      resource.isInfoWindowLocked = false;
      infoWindow.close();
      return;
    }
    resource.isInfoWindowLocked = true;
    infoWindow.open(this.bbxMarkers().at(index), false);
  }

  protected onClickBbxInfoWindow(index: number) {
    this.bbxInfoWindowClick.emit({
      index,
      resource: this.bbx().at(index),
    });
    const infoWindow = this.bbxInfoWindows().at(index);
    if (!infoWindow) {
      return;
    }
    infoWindow.close();
  }

  protected onClickTagMarker(index: number) {
    this.tagMarkerClick.emit({
      index,
      resource: this.tags().at(index),
    });
    const infoWindow = this.tagInfoWindows().at(index);
    if (!infoWindow) {
      return;
    }
    if (infoWindow.infoWindow.isOpen) {
      infoWindow.close();
      return;
    }
    infoWindow.open(this.tagMarkers().at(index), false);
  }

  protected onClickTagInfoWindow(index: number) {
    this.tagInfoWindowClick.emit({
      index,
      resource: this.tags().at(index),
    });
    const infoWindow = this.tagInfoWindows().at(index);
    if (!infoWindow) {
      return;
    }
    infoWindow.close();
  }

  protected onClickGeofenceCircle(
    index: number,
    $event: google.maps.MapMouseEvent,
  ) {
    const site = this.sites().at(index);
    if (!site) {
      return;
    }
    this.onClickGeofence(index, site, $event);
  }

  protected onClickGeofencePolygon(
    index: number,
    $event: google.maps.MapMouseEvent,
  ) {
    const site = this.sites().at(index);
    if (!site) {
      return;
    }
    this.onClickGeofence(index, site, $event);
  }

  protected onClickGeofence(
    index: number,
    resource: CartographyResourceSite,
    event: google.maps.MapMouseEvent,
  ) {
    this.geofenceClick.emit({
      index,
      resource,
      event,
    });
  }

  openInfoWindowOnMouseEnter(resource: CartographyResource) {
    const index = this.bbx().findIndex((bbx) => bbx.id === resource.id);
    this.bbxInfoWindows().at(index).open(this.bbxMarkers().at(index), false);
  }

  closeInfoWindowOnMouseLeave(resource: CartographyResource) {
    const index = this.bbx().findIndex((bbx) => bbx.id === resource.id);
    if (this.bbx().at(index).isInfoWindowLocked) {
      return;
    }
    this.bbxInfoWindows().at(index).close();
  }
}
